import api from './api';

export interface DashboardStats {
  totalUsers: number;
  totalProducts: number;
  totalInterventions: number;
  totalOrders: number;
  activeContracts: number;
  openComplaints: number;
  userGrowth: number;
  orderGrowth: number;
  revenue: {
    current: number;
    previous: number;
    growth: number;
  };
  recentActivities: Array<{
    id: number;
    type: string;
    title: string;
    description: string;
    timestamp: string;
    user: string;
  }>;
}

export interface PaymentStats {
  revenue: {
    today: number;
    week: number;
    month: number;
  };
  pending: {
    orders: { count: number; amount: number };
    diagnostics: { count: number; amount: number };
    subscriptions: { count: number; amount: number };
    total: { count: number; amount: number };
  };
}

export interface UnpaidDiagnostic {
  id: number;
  title: string;
  type: string;
  status: string;
  diagnostic_fee: number;
  scheduled_date: string;
  address: string;
  customer: {
    id: number;
    name: string;
    email: string;
    phone: string;
  } | null;
  technician: {
    id: number;
    name: string;
  } | null;
}

export interface ExpiringSubscription {
  id: number;
  customer: {
    id: number;
    name: string;
    email: string;
    phone: string;
  } | null;
  offer_name: string;
  price: number;
  start_date: string;
  end_date: string;
  days_remaining: number;
  payment_status: string;
}

export interface QuickStats {
  interventions: {
    today: number;
    pendingAssignment: number;
    inProgress: number;
  };
  openComplaints: number;
  pendingOrders: number;
  newCustomers: number;
  pendingContactRequests?: number;
}

class DashboardService {
  // Récupérer les statistiques du tableau de bord
  async getDashboardStats(): Promise<DashboardStats> {
    try {
      const response = await api.get<{ data: DashboardStats }>('/dashboard/stats');
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques du tableau de bord:', error);
      throw error;
    }
  }

  // Récupérer les activités récentes
  async getRecentActivities(limit: number = 5) {
    try {
      const response = await api.get('/activities/recent', {
        params: { limit }
      });
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des activités récentes:', error);
      return [];
    }
  }

  // Récupérer les statistiques de paiement
  async getPaymentStats(): Promise<PaymentStats> {
    try {
      const response = await api.get<{ success: boolean; data: PaymentStats }>('/admin/dashboard/payment-stats');
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques de paiement:', error);
      throw error;
    }
  }

  // Récupérer les diagnostics non payés
  async getUnpaidDiagnostics(limit: number = 10): Promise<UnpaidDiagnostic[]> {
    try {
      const response = await api.get<{ success: boolean; data: UnpaidDiagnostic[] }>('/admin/dashboard/unpaid-diagnostics', {
        params: { limit }
      });
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des diagnostics non payés:', error);
      throw error;
    }
  }

  // Récupérer les abonnements expirants
  async getExpiringSubscriptions(days: number = 7, limit: number = 10): Promise<ExpiringSubscription[]> {
    try {
      const response = await api.get<{ success: boolean; data: ExpiringSubscription[] }>('/admin/dashboard/expiring-subscriptions', {
        params: { days, limit }
      });
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des abonnements expirants:', error);
      throw error;
    }
  }

  // Récupérer les statistiques rapides
  async getQuickStats(): Promise<QuickStats> {
    try {
      const response = await api.get<{ success: boolean; data: QuickStats }>('/admin/dashboard/quick-stats');
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques rapides:', error);
      throw error;
    }
  }

  // Récupérer les demandes de rappel en attente (Hors-horaires)
  async getPendingCallbacks(limit: number = 50): Promise<any[]> {
    try {
      const response = await api.get<{ success: boolean; data: any[] }>('/admin/dashboard/pending-callbacks', {
        params: { limit }
      });
      return response.data.data;
    } catch (error) {
      console.error('Erreur lors de la récupération des demandes de rappel en attente:', error);
      return [];
    }
  }

  // Signaler qu'un rappel a été effectué lors de la reprise
  async resolveCallback(id: number, notes?: string): Promise<any> {
    try {
      const response = await api.patch(`/admin/dashboard/callbacks/${id}/resolve`, { status: 'resolved', notes });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du traitement du rappel ${id}:`, error);
      throw error;
    }
  }

  // Mettre à jour le statut d'un rappel (ex: passer en cours ou résolu avec notes)
  async updateCallbackStatus(id: number, status: string, notes?: string): Promise<any> {
    try {
      const response = await api.patch(`/admin/dashboard/callbacks/${id}/resolve`, { status, notes });
      return response.data;
    } catch (error) {
      console.error(`Erreur lors du changement de statut du rappel ${id}:`, error);
      throw error;
    }
  }

  // Vérifier la santé globale du système (API, PostgreSQL, Redis)
  async getSystemHealth(): Promise<{ status: string; timestamp: string; database: string; redis?: string }> {
    try {
      const response = await api.get('/ready');
      return response.data;
    } catch (error) {
      console.error('Erreur lors de la vérification de la santé du système:', error);
      return {
        status: 'NOT_READY',
        timestamp: new Date().toISOString(),
        database: 'disconnected',
        redis: 'disconnected'
      };
    }
  }

  // Récupérer les logs serveur Winston en temps réel
  async getSystemLogs(params?: { type?: 'error' | 'combined'; lines?: number; search?: string; level?: string }): Promise<SystemLogsResponse> {
    try {
      const response = await api.get('/admin/system/logs', { params });
      return response.data;
    } catch (error) {
      console.error('Erreur lors du chargement des logs système:', error);
      return {
        success: false,
        type: params?.type || 'error',
        total: 0,
        returned: 0,
        logs: [],
        message: 'Impossible de charger les logs système.'
      };
    }
  }
}

export interface SystemLogEntry {
  timestamp: string;
  level: string;
  message: string;
  correlationId?: string;
  method?: string;
  url?: string;
  status?: number;
  ip?: string;
  user?: any;
  stack?: string;
  [key: string]: any;
}

export interface SystemLogsResponse {
  success: boolean;
  type: string;
  total: number;
  returned: number;
  logs: SystemLogEntry[];
  message?: string;
}

export const dashboardService = new DashboardService();
